import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import toolService from '../services/toolService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const ToolForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    toolName: '',
    toolCode: '',
    category: 'Hand Tools',
    condition: 'good',
    status: 'available',
    purchaseDate: '',
    purchasePrice: '',
    serialNumber: '',
    manufacturer: '',
  });

  const categories = [
    'Hand Tools',
    'Power Tools',
    'Measuring Instruments',
    'Safety Equipment',
    'Diagnostic Tools',
    'Welding Equipment',
    'Other',
  ];

  const conditions = ['excellent', 'good', 'fair', 'needs_repair', 'damaged'];

  useEffect(() => {
    if (isEdit) {
      fetchTool();
    }
  }, [id]);

  const fetchTool = async () => {
    try {
      const response = await toolService.getById(id);
      const tool = response.data;
      setFormData({
        toolName: tool.toolName,
        toolCode: tool.toolCode,
        category: tool.category,
        condition: tool.condition,
        status: tool.status,
        purchaseDate: tool.purchaseDate ? tool.purchaseDate.split('T')[0] : '',
        purchasePrice: tool.purchasePrice || '',
        serialNumber: tool.serialNumber || '',
        manufacturer: tool.manufacturer || '',
      });
    } catch (error) {
      toast.error('Failed to fetch tool details');
      navigate('/tools');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submitData = {
      toolName: formData.toolName,
      toolCode: formData.toolCode,
      category: formData.category,
      condition: formData.condition,
      status: formData.status,
      purchaseDate: formData.purchaseDate || undefined,
      purchasePrice: formData.purchasePrice ? Number(formData.purchasePrice) : undefined,
      serialNumber: formData.serialNumber,
      manufacturer: formData.manufacturer,
    };

    try {
      if (isEdit) {
        await toolService.update(id, submitData);
        toast.success('Tool updated successfully');
      } else {
        await toolService.create(submitData);
        toast.success('Tool added successfully');
      }
      navigate('/tools');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate('/tools')} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">
          {isEdit ? 'Edit Tool' : 'Add New Tool'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Tool Information</h3>
          <div className="form-grid">
            <Input
              label="Tool Name"
              type="text"
              name="toolName"
              value={formData.toolName}
              onChange={handleChange}
              placeholder="Digital Multimeter"
              required
            />

            <Input
              label="Tool Code"
              type="text"
              name="toolCode"
              value={formData.toolCode}
              onChange={handleChange}
              placeholder="TOOL001"
              required
              disabled={isEdit}
            />

            <div className="input-group">
              <label className="input-label">
                Category <span className="required-asterisk">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="input-field"
                required
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Manufacturer"
              type="text"
              name="manufacturer"
              value={formData.manufacturer}
              onChange={handleChange}
              placeholder="Fluke"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Condition & Status</h3>
          <div className="form-grid">
            <div className="input-group">
              <label className="input-label">
                Condition <span className="required-asterisk">*</span>
              </label>
              <select
                name="condition"
                value={formData.condition}
                onChange={handleChange}
                className="input-field"
                required
              >
                {conditions.map((cond) => (
                  <option key={cond} value={cond}>
                    {cond.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label className="input-label">
                Status <span className="required-asterisk">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field"
                required
              >
                <option value="available">Available</option>
                <option value="in_use">In Use</option>
                <option value="under_maintenance">Under Maintenance</option>
                <option value="lost">Lost</option>
              </select>
            </div>

            <Input
              label="Serial Number"
              type="text"
              name="serialNumber"
              value={formData.serialNumber}
              onChange={handleChange}
              placeholder="SN123456"
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Purchase Details</h3>
          <div className="form-grid">
            <Input
              label="Purchase Date"
              type="date"
              name="purchaseDate"
              value={formData.purchaseDate}
              onChange={handleChange}
            />

            <Input
              label="Purchase Price (₹)"
              type="number"
              name="purchasePrice"
              value={formData.purchasePrice}
              onChange={handleChange}
              placeholder="5000"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/tools')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiSave />}
            loading={loading}
          >
            {isEdit ? 'Update Tool' : 'Add Tool'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ToolForm;
